import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";

/**
 * What AeroGauge can and cannot do in each customer's Microsoft 365 tenant.
 *
 * WHY THIS SCREEN EXISTS
 * A technician found a missing Microsoft 365 permission the only way that was
 * available to him: he tried to reset a password and it failed with an opaque
 * error. Eleven of thirteen tenants had the same gap, and nobody knew, because
 * the tenant list showed one green tick per row for "connected".
 *
 * A tenant is not connected-or-not. It has a set of capabilities that depends
 * on which permissions were consented and which directory roles the app holds
 * at the moment it was onboarded, and those drift as the product gains
 * features. This says so before somebody needs it.
 *
 * Deliberately three states per capability, not two: yes, no, and UNKNOWN.
 * A red cross because Graph timed out would send an admin into Entra ID to
 * re-consent permissions that were never missing.
 */

type Problem = {
  code: string;
  severity: "error" | "warning" | "info";
  message: string;
  detail?: string;
};

type Report = {
  customer_id: number;
  customer_name: string;
  tenant_id: string;
  display_name: string | null;
  reachable: boolean;
  roles: string[] | null;
  granted_permissions?: string[];
  missing_permissions: string[];
  capabilities: Record<string, boolean | null>;
  problems: Problem[];
};

type Health = {
  summary: {
    total: number;
    healthy: number;
    warning: number;
    error: number;
    needs_attention: string[];
  };
  tenants: Report[];
};

// Ordered by how often a service desk needs it, not alphabetically.
const CAPABILITY_LABELS: [string, string][] = [
  ["reset_password", "Reset a password"],
  ["reset_mfa", "Reset MFA"],
  ["disable_enable_user", "Disable / re-enable a user"],
  ["reset_admin_password", "Reset an admin's password"],
  ["manage_groups", "Manage groups"],
  ["mailbox_rules", "Mailbox rules & forwarding"],
  ["exchange_powershell", "Exchange PowerShell"],
  ["sharepoint_sites", "SharePoint sites"],
  ["onedrive_files", "OneDrive files"],
  ["intune_actions", "Intune device actions"],
];

function Cap({ value }: { value: boolean | null | undefined }) {
  if (value === true)
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
        <span aria-hidden>✓</span> Yes
      </span>
    );
  if (value === false)
    return (
      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
        <span aria-hidden>✕</span> No
      </span>
    );
  // Unknown. Said plainly rather than shown as a failure - see the header
  // comment; the two have different consequences for the reader.
  return (
    <span className="text-gray-400 dark:text-gray-500" title="Could not determine">
      Unknown
    </span>
  );
}

const SEVERITY_STYLES: Record<Problem["severity"], string> = {
  error:
    "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300",
  warning:
    "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900 text-amber-900 dark:text-amber-300",
  info: "bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-900 text-blue-800 dark:text-blue-300",
};

export default function M365TenantHealth() {
  const [data, setData] = useState<Health | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/api/v1/m365/tenants/health", {
        params: refresh ? { refresh: 1 } : undefined,
      });
      setData(r.data);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Could not check tenant permissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data)
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 text-sm text-gray-500 dark:text-gray-400">
        Checking what AeroGauge can do in each tenant…
      </div>
    );

  if (error)
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4">
        <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
      </div>
    );

  if (!data) return null;

  const { summary, tenants } = data;
  const attention = summary.error + summary.warning;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            Tenant permissions
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            What AeroGauge is actually allowed to do in each customer's Microsoft 365
            tenant. Checked live.
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={loading}
          className="ml-auto text-sm border border-gray-300 dark:border-gray-600 text-gray-700
                     dark:text-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50
                     dark:hover:bg-gray-700/40 disabled:opacity-50"
        >
          {loading ? "Checking…" : "Re-check"}
        </button>
      </div>

      {/* One line, counting TENANTS rather than problems: a single
          badly-configured tenant can raise four problems on its own, and
          "11 problems" is not something anybody can act on. */}
      <div className="px-4 py-3 text-sm border-b border-gray-100 dark:border-gray-700">
        {attention === 0 ? (
          <span className="text-emerald-700 dark:text-emerald-400">
            All {summary.total} tenants can do everything AeroGauge expects.
          </span>
        ) : (
          <span className="text-gray-700 dark:text-gray-300">
            <strong>
              {attention} of {summary.total}
            </strong>{" "}
            {attention === 1 ? "tenant needs" : "tenants need"} attention
            {summary.error > 0 && (
              <span className="text-red-600 dark:text-red-400">
                {" "}
                ({summary.error} with a feature that will fail)
              </span>
            )}
            .
          </span>
        )}
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {tenants.map((t) => {
          const worst = t.problems.some((p) => p.severity === "error")
            ? "error"
            : t.problems.some((p) => p.severity === "warning")
              ? "warning"
              : null;
          const isOpen = open === t.customer_id;
          return (
            <div key={t.customer_id}>
              <button
                onClick={() => setOpen(isOpen ? null : t.customer_id)}
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50
                           dark:hover:bg-gray-700/40"
                aria-expanded={isOpen}
              >
                <span
                  className={`w-2 h-2 rounded-full shrink-0 ${
                    !t.reachable || worst === "error"
                      ? "bg-red-500"
                      : worst === "warning"
                        ? "bg-amber-500"
                        : "bg-emerald-500"
                  }`}
                  aria-hidden
                />
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {t.customer_name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {!t.reachable
                    ? "cannot sign in"
                    : t.problems.length === 0
                      ? "all good"
                      : t.problems.length === 1
                        ? "1 note"
                        : `${t.problems.length} notes`}
                </span>
                <span className="ml-auto text-gray-400 text-xs">{isOpen ? "▲" : "▼"}</span>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 space-y-3">
                  {t.problems.map((p, i) => (
                    <div
                      key={i}
                      className={`text-sm px-3 py-2 rounded-lg border ${SEVERITY_STYLES[p.severity]}`}
                    >
                      {p.message}
                      {p.detail && (
                        <div className="mt-1 font-mono text-xs opacity-70">{p.detail}</div>
                      )}
                    </div>
                  ))}

                  {t.reachable && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      {CAPABILITY_LABELS.filter(([k]) => k in t.capabilities).map(
                        ([key, label]) => (
                          <div
                            key={key}
                            className="flex items-center justify-between gap-3 py-0.5"
                          >
                            <span className="text-gray-600 dark:text-gray-400">{label}</span>
                            <Cap value={t.capabilities[key]} />
                          </div>
                        ),
                      )}
                    </div>
                  )}

                  {t.roles && t.roles.length > 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Directory roles held: {t.roles.join(", ")}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
